/**
 * What the background sync remembers between runs, in localStorage (so every tab of a browser sees the same):
 * when a full sync last finished, when each table was last fetched, and the version (ETag) of each list the
 * local cache was filled from.
 *
 * Two ways these used to be wrong:
 *  - a time stamp from the future (the PC's clock was set back, or was wrong when it was written) made "synced
 *    N minutes ago" negative, so nothing was due again until the clock caught up and the timer only synced when
 *    the browser came back online; a stamp that was not a number kept the prescriptions from ever being due;
 *  - they survived logout and "clear offline data", so signing out and in again within four minutes found the
 *    sync "up to date" with an empty local cache.
 */

/** When a full sync last finished, in any tab of this browser. */
export const LAST_SYNC_KEY = 'clirxos-offline-last-sync';
const PREFIX = 'clirxos-sync-';
export const etagKey = (tableName: string) => `${PREFIX}etag:${tableName}`;
/** When the table was last fetched. */
export const tableKey = (tableName: string) => `${PREFIX}at:${tableName}`;

/** Set when this page has forgotten the sync state because the offline data are being deleted (see forgetSyncState). */
let forgotten = false;

export const readLocal = (key: string): string | null => { try { return localStorage.getItem(key); } catch { return null; } };
export const writeLocal = (key: string, value: string) => {
  if (forgotten) return;
  try { localStorage.setItem(key, value); } catch { /* private mode: no skipping, still correct */ }
};

/**
 * How long ago the time stamp under `key` was written, in ms. A stamp that is missing, is not a time, or is in the
 * future counts as "long ago": the work it guards is due.
 */
export function stampAge(key: string, now = Date.now()): number {
  const age = now - Number(readLocal(key));
  return Number.isFinite(age) && age >= 0 ? age : Infinity;
}

/**
 * Forget all of it, so the next sync fills the cache instead of finding it "up to date". Called when the local
 * cache is wiped (logout, the leftovers of an ended session), in the page that wipes it and, by message, in the
 * other tabs of the browser. From then on this page records none of it again: a sync that was already running
 * (the file is deleted while it writes, in this tab or another) would otherwise stamp the emptied cache "current"
 * the moment it ended. The page is on its way to the login page, which loads afresh.
 */
export function forgetSyncState(): void {
  forgotten = true;
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && (key === LAST_SYNC_KEY || key.startsWith(PREFIX))) localStorage.removeItem(key);
    }
  } catch { /* no storage: nothing was kept */ }
}
